import * as React from 'react';
import { Loader2, MailPlus, RotateCw, Trash2, UserPlus, X } from 'lucide-react';
import type { AdminUser } from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

/**
 * Admin console users. Inviting emails a link to /accept-invite where the
 * new admin sets their own password; removing takes admin access away
 * immediately (open sessions included).
 */
export function UsersPage() {
  const [items, setItems] = React.useState<AdminUser[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [inviting, setInviting] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    client.admin.users
      .list()
      .then((r) => {
        setItems(r.items);
        setError(null);
      })
      .catch((err: Error) => setError(err.message));
  }, []);
  React.useEffect(load, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice((n) => (n === msg ? null : n)), 5000);
  };

  const active = items?.filter((u) => u.status === 'active') ?? [];
  const pending = items?.filter((u) => u.status !== 'active') ?? [];

  return (
    <AppShell>
      <div className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Admin / Users</div>
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Admin users
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              Everyone with access to this console. Invites expire after 7 days.
            </p>
          </div>
          <Button onClick={() => setInviting(true)}>
            <UserPlus />
            Invite admin
          </Button>
        </div>

        {notice && (
          <Card className="border-success/30 bg-success/10 px-4 py-3 font-mono text-[11px] text-success">
            {notice}
          </Card>
        )}
        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}
        {!items && !error && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">Loading…</Card>
        )}

        {items && (
          <>
            <UserList
              title="Admins"
              count={active.length}
              users={active}
              onChanged={load}
              onNotice={flash}
            />
            {pending.length > 0 && (
              <UserList
                title="Pending invites"
                count={pending.length}
                users={pending}
                onChanged={load}
                onNotice={flash}
              />
            )}
          </>
        )}
      </div>
      {inviting && (
        <InviteDialog
          onClose={() => setInviting(false)}
          onInvited={(email, emailSent) => {
            setInviting(false);
            flash(
              emailSent
                ? `Invite sent to ${email}.`
                : `Invite created for ${email}, but the email couldn’t be sent — use Resend.`,
            );
            load();
          }}
        />
      )}
    </AppShell>
  );
}

function UserList({
  title,
  count,
  users,
  onChanged,
  onNotice,
}: {
  title: string;
  count: number;
  users: AdminUser[];
  onChanged: () => void;
  onNotice: (msg: string) => void;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-[hsl(var(--hairline))] px-5 py-3">
        <span className="mono-label text-muted-foreground">{title}</span>
        <span className="pill pill-muted">{count}</span>
      </div>
      {users.length === 0 ? (
        <div className="p-6 font-mono text-[11px] text-muted-foreground">None.</div>
      ) : (
        users.map((u) => (
          <UserRow key={u._id} user={u} onChanged={onChanged} onNotice={onNotice} />
        ))
      )}
    </Card>
  );
}

function UserRow({
  user: u,
  onChanged,
  onNotice,
}: {
  user: AdminUser;
  onChanged: () => void;
  onNotice: (msg: string) => void;
}) {
  const [busy, setBusy] = React.useState<'resend' | 'remove' | null>(null);
  const [armed, setArmed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isPending = u.status !== 'active';

  React.useEffect(() => {
    if (!armed) return;
    const t = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(t);
  }, [armed]);

  const run = async (kind: 'resend' | 'remove') => {
    if (kind === 'remove' && !armed) return setArmed(true);
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'resend') {
        const r = await client.admin.users.resendInvite(u._id);
        if (!r.emailSent) throw new Error('The email couldn’t be sent — try again in a moment.');
        onNotice(`New invite sent to ${u.email}.`);
      } else {
        await client.admin.users.remove(u._id);
        onNotice(isPending ? `Invite for ${u.email} cancelled.` : `${u.name ?? u.email} is no longer an admin.`);
      }
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusy(null);
      setArmed(false);
    }
  };

  const date = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const meta = isPending
    ? [u.invitedBy && `Invited by ${u.invitedBy}`, date(u.invitedAt)].filter(Boolean).join(' · ')
    : [date(u.createdAt) && `Since ${date(u.createdAt)}`, u.invitedBy && `invited by ${u.invitedBy}`]
        .filter(Boolean)
        .join(' · ');

  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-4 last:border-0">
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-mono text-[12px] font-bold uppercase',
          isPending ? 'border border-dashed border-border text-muted-foreground' : 'bg-muted',
        )}
      >
        {isPending ? <MailPlus className="h-4 w-4" /> : initials(u.name ?? u.email)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[14px] font-medium">{u.name ?? u.email}</span>
          {u.isSelf && <span className="pill pill-accent">You</span>}
          {u.status === 'invited' && <span className="pill pill-warning">Invited</span>}
          {u.status === 'expired' && <span className="pill pill-danger">Invite expired</span>}
        </div>
        <div className="truncate font-mono text-[11px] text-muted-foreground">
          {u.name ? u.email : null}
          {u.name && meta ? ' · ' : null}
          {meta}
        </div>
        {error && <div className="mt-1 text-[12px] text-destructive">{error}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isPending && (
          <Button variant="outline" size="sm" onClick={() => void run('resend')} disabled={busy !== null}>
            {busy === 'resend' ? <Loader2 className="animate-spin" /> : <RotateCw />}
            Resend
          </Button>
        )}
        {!u.isSelf && (
          <Button
            variant={armed ? 'destructive' : 'ghost'}
            size="sm"
            onClick={() => void run('remove')}
            disabled={busy !== null}
          >
            {busy === 'remove' ? <Loader2 className="animate-spin" /> : <Trash2 />}
            {armed ? 'Click to confirm' : isPending ? 'Cancel invite' : 'Remove'}
          </Button>
        )}
      </div>
    </div>
  );
}

function InviteDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: (email: string, emailSent: boolean) => void;
}) {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.admin.users.invite({
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onInvited(email.trim().toLowerCase(), r.emailSent);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <Card className="relative w-full max-w-md p-0 shadow-pop">
        <form onSubmit={(e) => void submit(e)}>
          <div className="flex items-start justify-between border-b border-[hsl(var(--hairline))] px-5 py-4">
            <div>
              <h2 className="font-display text-[20px] font-medium leading-none">Invite admin</h2>
              <p className="mt-1.5 text-[12px] text-muted-foreground">
                They’ll get an email to set their own password. The link works for 7 days.
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X />
            </Button>
          </div>
          <div className="space-y-4 px-5 py-5">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@proptr.ee"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="invite-name">Name (optional)</Label>
              <Input
                id="invite-name"
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="They can change it when they accept"
                maxLength={80}
              />
            </div>
            {error && <p className="text-[12px] text-destructive">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || busy}>
              {busy && <Loader2 className="animate-spin" />}
              Send invite
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
